import React, { useCallback, useEffect, useState } from 'react';

import { noop } from 'lodash';
import PropTypes from 'prop-types';

import ExtendedInputCheckbox from '../ExtendedInputCheckbox';

const days = {
  monday: 'Montag',
  tuesday: 'Dienstag',
  wednesday: 'Mittwoch',
  thursday: 'Donnerstag',
  friday: 'Freitag',
};

const times = {
  morning: 'Vormittag',
  afternoon: 'Nachmittag',
};

export const options = [
  { day: 'monday', part: 'am' },
  { day: 'monday', part: 'pm' },
  { day: 'tuesday', part: 'am' },
  { day: 'tuesday', part: 'pm' },
  { day: 'wednesday', part: 'am' },
  { day: 'wednesday', part: 'pm' },
  { day: 'thursday', part: 'am' },
  { day: 'thursday', part: 'pm' },
  { day: 'friday', part: 'am' },
  { day: 'friday', part: 'pm' },
];

const SchedulePicker = ({
  enabledOptions = [],
  initialSchedule,
  minDaysRequired = 0,
  onChange = noop,
  readOnly,
}) => {
  const [schedule, setSchedule] = useState({
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
  });

  useEffect(() => {
    setSchedule({
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      ...initialSchedule,
    });
  }, [initialSchedule]);

  const daysChecked =
    schedule.monday.length +
    schedule.tuesday.length +
    schedule.wednesday.length +
    schedule.thursday.length +
    schedule.friday.length;

  const isDisabledPart = (day, part) =>
    enabledOptions?.length &&
    !enabledOptions?.find(option => option.day === day && option.part === part);

  const isDisabledDay = day =>
    enabledOptions?.length && !enabledOptions?.find(option => option.day === day);

  const handleChange = useCallback(
    (day, event) => {
      const updateSchedule = { ...schedule };

      if (event.target.checked) {
        updateSchedule[day] = [...updateSchedule[day], `${event.target.value}`];
      } else {
        updateSchedule[day] = updateSchedule[day].filter(time => time !== event.target.value);
      }
      setSchedule(updateSchedule);
      onChange(updateSchedule);
    },
    [schedule],
  );

  return (
    <div className="overflow-auto rounded border border-solid border-slate-200 bg-white">
      <table className="w-full">
        <tbody>
          <tr>
            <td>&nbsp;</td>
            {Object.entries(times).map(([key, value]) => (
              <td key={key} className="py-2 pr-4 text-center">
                {value}
              </td>
            ))}
          </tr>
          {Object.entries(days).map(([key, value]) => {
            const day = schedule[key];

            const dayIncludesAm = day.some(item => item === 'am');
            const dayIncludesPm = day.some(item => item === 'pm');

            const preventUncheck = daysChecked <= minDaysRequired;
            const readOnlyAm = readOnly || (dayIncludesAm && preventUncheck);
            const readOnlyPm = readOnly || (dayIncludesPm && preventUncheck);

            return (
              (!readOnly || !!day.length) && (
                <tr
                  className={`${isDisabledDay(key) ? 'hidden' : 'border-t border-slate-200'}`}
                  key={key}
                >
                  <td className="py-1 px-4 font-semibold">{value}</td>
                  <td className="py-1 pr-4 text-center">
                    <span
                      className={`${
                        isDisabledPart(key, 'am')
                          ? 'hidden'
                          : 'flex h-full items-center justify-center'
                      }`}
                    >
                      {(!readOnlyAm || !!dayIncludesAm) && (
                        <ExtendedInputCheckbox
                          checked={dayIncludesAm}
                          className={`h-5 w-5 text-blue-600 ${
                            readOnlyAm ? 'cursor-not-allowed' : 'cursor-pointer'
                          }`}
                          readOnly={readOnlyAm}
                          onChange={event => handleChange(key, event)}
                          value="am"
                        />
                      )}
                    </span>
                  </td>
                  <td className="py-1 pr-4 text-center">
                    <span
                      className={`${
                        isDisabledPart(key, 'pm')
                          ? 'hidden'
                          : 'flex h-full items-center justify-center'
                      }`}
                    >
                      {(!readOnlyPm || !!dayIncludesPm) && (
                        <ExtendedInputCheckbox
                          checked={dayIncludesPm}
                          className={`h-5 w-5 text-blue-600 ${
                            readOnlyPm ? 'cursor-not-allowed' : 'cursor-pointer'
                          }`}
                          readOnly={readOnlyPm}
                          onChange={event => handleChange(key, event)}
                          value="pm"
                        />
                      )}
                    </span>
                  </td>
                </tr>
              )
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

SchedulePicker.propTypes = {
  enabledOptions: PropTypes.array,
  initialSchedule: PropTypes.shape({
    monday: PropTypes.array,
    tuesday: PropTypes.array,
    wednesday: PropTypes.array,
    thursday: PropTypes.array,
    friday: PropTypes.array,
  }),
  minDaysRequired: PropTypes.number,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
};

export default SchedulePicker;
